import Box from "@/common/components/Box";
import { ImageCarousel } from "@/common/components/ImageCarousel";
import { ImageWithPlaceholder } from "@/common/components/ImageWithPlaceholder";
import { ChevronDown, ChevronUp, WrenchIcon } from "lucide-react";
import ResumeItem from "@/common/components/ResumeItem";
import { formatCurrency, formatUnit } from "@/common/utils/formatters";
import { DiagnosticActivityData } from "@/features/maintenance/features/workflows/types/forwardings/directiveEntries/diagnosticEntry/diagnosticEntry.type";
import DeleteDiagnosticObjectiveActivity from "./DeleteDiagniosticObjectiveActivity";
import UpdateDiagnosticObjectiveActivity from "./UpdateDiagnosticObjectiveActivity";
import Button from "@/common/components/Button";
import { useState } from "react";

const DiagnosticObjectiveActivityItem = ({
  diagnosticActivity,
}: {
  diagnosticActivity: DiagnosticActivityData;
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const quantityDisplay =
    diagnosticActivity.unit_for_quantity === "minutes"
      ? formatUnit(diagnosticActivity.quantity, "hours")
      : formatUnit(diagnosticActivity.quantity, "unit");

  return (
    <Box className="p-4">
      {isCollapsed ? (
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <ImageCarousel
              files={
                diagnosticActivity.images?.map((image) => ({
                  id: image.id,
                  file: image?.url,
                })) || []
              }
              Placeholder={
                <ImageWithPlaceholder
                  className="size-10 rounded-md"
                  iconClassName="size-5"
                  placeholderIcon={WrenchIcon}
                />
              }
              className="size-10 shrink-0 bg-[#fff]"
              imageClassName="object-contain"
              showExpandButton
              showRemoveButton={false}
            />

            <p className="text-sm font-bold truncate">
              {diagnosticActivity?.display_name || "--"}
            </p>

            <div className="flex items-center gap-2 text-xs text-gray-400 shrink-0">
              <span>{formatCurrency(Number(diagnosticActivity?.unit_price) || 0)}</span>
              <span>×</span>
              <span>{quantityDisplay}</span>
              <span>=</span>
              <span className="text-valid font-semibold">
                {formatCurrency(Number(diagnosticActivity?.total_value) || 0)}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <DeleteDiagnosticObjectiveActivity
              diagnosticActivity={diagnosticActivity}
            />

            <UpdateDiagnosticObjectiveActivity
              diagnosticActivity={diagnosticActivity}
            />

            <Button variant="link-white" onClick={() => setIsCollapsed(false)}>
              <ChevronDown strokeWidth={1} className="text-white" />
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-start justify-between gap-4 flex-1">
          <div className="flex items-start gap-4">
            <ImageCarousel
              files={
                diagnosticActivity.images?.map((image) => ({
                  id: image.id,
                  file: image?.url,
                })) || []
              }
              Placeholder={
                <ImageWithPlaceholder
                  className="size-28 rounded-md"
                  iconClassName="size-10"
                  placeholderIcon={WrenchIcon}
                />
              }
              className="size-28 bg-[#fff]"
              imageClassName="object-contain"
              showExpandButton
              showRemoveButton={false}
              maxDots={5}
            />

            <div className="flex w-full flex-col gap-2">
              <p className="text-base font-bold">
                {diagnosticActivity?.display_name || "--"}
              </p>

              <div className="flex items-center flex-wrap gap-3">
                {diagnosticActivity.unit_for_quantity === "minutes" ? (
                  <ResumeItem
                    className="text-sm"
                    label="Valor por hora"
                    value={formatCurrency(Number(diagnosticActivity?.unit_price) || 0)}
                  />
                ) : (
                  <ResumeItem
                    className="text-sm"
                    label="Valor unt."
                    value={formatCurrency(Number(diagnosticActivity?.unit_price) || 0)}
                  />
                )}

                <p className="text-sm">x</p>

                {diagnosticActivity.unit_for_quantity === "minutes" ? (
                  <ResumeItem
                    className="text-sm"
                    label="Tempo de execução"
                    value={formatUnit(diagnosticActivity.quantity, "hours")}
                  />
                ) : (
                  <ResumeItem
                    className="text-sm"
                    label="Unidades"
                    value={formatUnit(diagnosticActivity.quantity, "unit")}
                  />
                )}

                <p className="text-sm">=</p>

                <ResumeItem
                  className="text-sm"
                  label="Valor total"
                  value={formatCurrency(
                    Number(diagnosticActivity?.total_value) || 0,
                  )}
                />

                <ResumeItem
                  className="text-sm"
                  label="Garantia"
                  value={`${diagnosticActivity.warranty_in_days} Dias`}
                />
              </div>

              <div className="flex w-full flex-wrap gap-3">
                <ResumeItem
                  className="text-sm"
                  label="Profissional"
                  value={
                    diagnosticActivity.technical_professionals
                      ?.map(({ display_name }) => display_name)
                      ?.join(", ") || "--"
                  }
                />

                <ResumeItem
                  className="text-sm"
                  label="Descrição"
                  value={diagnosticActivity.description}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col items-end gap-4">
            <Button variant="link-white" onClick={() => setIsCollapsed(true)}>
              <ChevronUp strokeWidth={1} className="text-white" />
            </Button>

            <div className="ml-auto flex gap-3">
              <DeleteDiagnosticObjectiveActivity
                diagnosticActivity={diagnosticActivity}
              />

              <UpdateDiagnosticObjectiveActivity
                diagnosticActivity={diagnosticActivity}
              />
            </div>
          </div>
        </div>
      )}
    </Box>
  );
};

export default DiagnosticObjectiveActivityItem;
